import React, {useState, useEffect, useRef, useCallback} from 'react';
import L from 'leaflet';
import {ThemeProvider, createTheme} from '@mui/material/styles';
import {Box, Typography, Button} from '@mui/material';
import SPARQLQueryDispatcher from './request/SPARQLQueryDispatcher';
import SPARQLQuery from './request/SPARQLQuery';
import HierarchySPARQLQuery from './request/HierarchySPARQLQuery';
import allLanguagesData from "./res/languages_nativelabel_optional_more_filters.json";
import Language from "./models/Language";
import AddedLabel from "./models/AddedLabel";
import Place from "./models/Place";
import {PLACE_TYPES, DEFAULT_PLACE_TYPES} from "./models/PlaceType"
import Header from "./components/Header";
import InteractiveMap from "./components/InteractiveMap";
import ControlPanel from "./components/ControlPanel";
import LocationNameDialog from "./components/LocationNameDialog";
import WikidataUploadDialog from "./components/WikidataUploadDialog";
import HierarchyDialog from "./components/HierarchyDialog";
import EndonymsList, {focusMapOnPlace} from "./components/EndonymsList";
import AlertMessages from "./components/AlertMessages";
import WelcomeDialog from "./components/WelcomeDialog";
import InfoDialog from "./components/InfoDialog";
import SideMenu from "./components/SideMenu";
import BadgeSystem from "./components/BadgeSystem";
import StatisticsDashboard from "./components/StatisticsDashboard";
import Statistics from "./models/Statistics";
import {addLabel, AddLabelStatus} from "./request/WikiDataApi"

// Modern linguistic research theme
const theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#2563eb', // Modern blue
            light: '#3b82f6',
            dark: '#1d4ed8',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#10b981', // Emerald green for success states
            light: '#34d399',
            dark: '#059669',
        },
        accent: {
            main: '#8b5cf6', // Purple for highlights
            light: '#a78bfa',
            dark: '#7c3aed',
        },
        background: {
            default: '#f8fafc',
            paper: '#ffffff',
        },
        text: {
            primary: '#1e293b',
            secondary: '#64748b',
        },
        error: {
            main: '#ef4444',
            light: '#f87171',
            dark: '#dc2626',
        },
        warning: {
            main: '#f59e0b',
            light: '#fbbf24',
            dark: '#d97706',
        },
        info: {
            main: '#06b6d4',
            light: '#22d3ee',
            dark: '#0891b2',
        },
        success: {
            main: '#10b981',
            light: '#34d399',
            dark: '#059669',
        },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
            fontSize: '2.5rem',
            fontWeight: 700,
            letterSpacing: '-0.025em',
        },
        h2: {
            fontSize: '2rem',
            fontWeight: 600,
            letterSpacing: '-0.025em',
        },
        h3: {
            fontSize: '1.5rem',
            fontWeight: 600,
            letterSpacing: '-0.025em',
        },
        h4: {
            fontSize: '1.25rem',
            fontWeight: 600,
        },
        h5: {
            fontSize: '1.125rem',
            fontWeight: 600,
        },
        h6: {
            fontSize: '1rem',
            fontWeight: 600,
        },
        body1: {
            fontSize: '1rem',
            lineHeight: 1.6,
        },
        body2: {
            fontSize: '0.875rem',
            lineHeight: 1.5,
        },
        button: {
            textTransform: 'none',
            fontWeight: 500,
        },
    },
    shape: {
        borderRadius: 12,
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    padding: '10px 24px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    boxShadow: 'none',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        transform: 'translateY(-1px)',
                    },
                    '&:active': {
                        transform: 'translateY(0px)',
                        transition: 'all 0.1s cubic-bezier(0.4, 0, 0.2, 1)',
                    },
                },
                contained: {
                    background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)',
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
                    '&.MuiPaper-elevation1': {
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    },
                    '&.MuiPaper-elevation2': {
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    },
                    '&.MuiPaper-elevation3': {
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 12,
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3b82f6',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#2563eb',
                            borderWidth: 2,
                        },
                    },
                },
            },
        },
        MuiCircularProgress: {
            styleOverrides: {
                root: {
                    animationDuration: '1.4s',
                },
            },
        },
    },
});


function App() {

    // Languages
    const [languages, setLanguages] = useState([]); // languages from static language JSON
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [customLanguageObject, setCustomLanguageObject] = useState(null);
    const [customLanguage, setCustomLanguage] = useState('');
    const [customLanguageCode, setCustomLanguageCode] = useState('');


    // Place Type Selection
    const [selectedPlaceTypes, setSelectedPlaceTypes] = useState(DEFAULT_PLACE_TYPES);
    
    // Include places with existing labels
    const [includeLabeled, setIncludeLabeled] = useState(false);

    // Map
    const mapRef = useRef();
    const initialMapCoordinates = [46.8182, 8.2275]; // Switzerland

    // Places
    const [places, setPlaces] = useState([]); // places from response
    const [savedPlaces, setSavedPlaces] = useState(new Set()); // all saved places by user

    // Dialog for Location Names
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedPlace, setSelectedPlace] = useState(null); // place object for dialog
    const [originalPlaceName, setOriginalPlaceName] = useState(''); // displayed in dialog title
    const [modifiedPlaceName, setModifiedPlaceName] = useState(''); // user input
    const [showEmptyInputError, setShowEmptyInputError] = useState(false);

    // Dialog for adding Location Names to WikiData
    const [openWikidataDialog, setOpenWikidataDialog] = useState(false);
    const [accessToken, setAccessToken] = useState("");
    const [uploadProgress, setUploadProgress] = useState(0);

    // Hierarchy Dialog
    const [openHierarchyDialog, setOpenHierarchyDialog] = useState(false);
    const [hierarchyData, setHierarchyData] = useState(null);
    const [hierarchyLoading, setHierarchyLoading] = useState(false);
    const [hierarchyError, setHierarchyError] = useState("");

    // Welcome Screen
    const [showWelcomeScreen, setShowWelcomeScreen] = useState(true);

    // Side Menu
    const [sideMenuOpen, setSideMenuOpen] = useState(false);
    
    // Info Dialog (controlled from side menu)
    const [infoDialogOpen, setInfoDialogOpen] = useState(false);
    
    // EndonymsList panel state
    const [endonymsListExpanded, setEndonymsListExpanded] = useState(false);

    // Gamification state
    const [statistics, setStatistics] = useState(new Statistics());
    const [badgeSystemOpen, setBadgeSystemOpen] = useState(false);
    const [statisticsOpen, setStatisticsOpen] = useState(false);
    const [newBadges, setNewBadges] = useState([]);

    // Fetching and Error
    const [isLoading, setIsLoading] = useState(false);
    const [noPlacesFoundMessage, setNoPlacesFoundMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [abortController, setAbortController] = useState(null);


    // Query
    const queryDispatcher = new SPARQLQueryDispatcher();

    // Focus
    const inputRef = useRef(null);

    useEffect(() => {
        // Initialize Language objects from JSON data, filtering out regional variants
        const filteredLanguageData = allLanguagesData.filter(langData => {
            const tag = langData.ietfLanguageTag || '';
            // Only include base language codes (without regional suffixes like -CH, -FR, etc.)
            return tag && !tag.includes('-');
        });
        
        const languageObjects = filteredLanguageData.map(langData => new Language(
            langData.language, // language = property name from response
            langData.languageLabel, // languageLabel = property name from response
            langData.ietfLanguageTag || '',
            langData.nativeLabels || '',
            "",
            ""
        ));
        setLanguages(languageObjects);

        // Always show welcome screen initially until language is selected
        // The welcome screen will handle the logic of staying open until language selection
        setShowWelcomeScreen(true);

        // Load statistics from localStorage
        const savedStats = localStorage.getItem('endolink_statistics');
        if (savedStats) {
            try {
                const statsData = JSON.parse(savedStats);
                setStatistics(Statistics.fromJSON(statsData));
            } catch (error) {
                console.error('Error loading statistics:', error);
                setStatistics(new Statistics());
            }
        }
    }, [selectedLanguage, customLanguageObject]);

    // Save statistics to localStorage
    const saveStatistics = (stats) => {
        try {
            localStorage.setItem('endolink_statistics', JSON.stringify(stats.toJSON()));
        } catch (error) {
            console.error('Error saving statistics:', error);
        }
    };

    const handleOnSearchClick = () => {
        setErrorMessage(''); // Reset previous error messages

        // Validate the selected language
        if (
            (!selectedLanguage?.ietfLanguageTag &&
                (!customLanguageObject?.customLabel || !customLanguageObject?.customLanguageCode))
        ) {
            setErrorMessage("Please select a language or add a missing one.");
            return;
        }

        if (mapRef.current) {
            const bounds = mapRef.current.getBounds();
            const center = mapRef.current.getCenter();
            const centerLat = center.lat;
            const centerLon = center.lng;
            
            // Calculate radius from center to corner (approximate) with max 25km limit
            const corner = bounds.getNorthEast();
            const calculatedRadiusKm = mapRef.current.distance([centerLat, centerLon], [corner.lat, corner.lng]) / 1000;
            const radiusKm = Math.min(Math.ceil(calculatedRadiusKm), 25); // Cap at 25km
            
            fetchMissingPlacesLabelDataSpatial(centerLat, centerLon, radiusKm);
        }
    };

    const handleStopQuery = () => {
        if (abortController) {
            abortController.abort();
            setAbortController(null);
            setIsLoading(false);
        }
    };

    const fetchMissingPlacesLabelDataSpatial = (centerLat, centerLon, radiusKm) => {
        setIsLoading(true);
        setErrorMessage(''); // Clear previous errors

        // Create new AbortController for this query
        const controller = new AbortController();
        setAbortController(controller);

        // Check if ietfLanguageTag exists; if not, use the custom code
        const languageCode = selectedLanguage?.ietfLanguageTag || customLanguageObject?.customLanguageCode;
        
        // Convert place type IDs to Wikidata Q-IDs
        const wikidataPlaceTypes = selectedPlaceTypes.map(typeId => {
            const placeType = PLACE_TYPES.find(type => type.id === typeId);
            return placeType ? placeType.wikidataId : typeId;
        }).filter(id => id && id.startsWith('Q')); // Filter out invalid IDs

        const sparqlQueryInstance = new SPARQLQuery(languageCode, 0, 0, 0, 0, wikidataPlaceTypes, includeLabeled);
        
        // Use the spatial query method for better performance
        const queryString = sparqlQueryInstance.getSpatialPlacesQuery(centerLat, centerLon, radiusKm);

        console.log('SPARQL Spatial Query:', queryString);
        console.log(`Center: ${centerLat}, ${centerLon}, Radius: ${radiusKm}km`);

        queryDispatcher
            .query(queryString, controller.signal)
            .then((data) => {
                // Map the response to Place objects
                const placesData = data.results.bindings.map((place) => {
                    const latLon = place.coordinates.value.slice(6, -1).split(' '); // Extract coordinates
                    return new Place(
                        place.place.value || "", // wikidataID
                        place.placeLabel?.value || "", // wikidataLabel
                        parseFloat(latLon[1]), // Latitude
                        parseFloat(latLon[0]), // Longitude
                        place.matchedType?.value || null, // matchedType Q-ID
                        place.matchedTypeLabel?.value || null, // matchedTypeLabel
                        place.existingLabel?.value || null // existingLabel in selected language
                    );
                });

                setPlaces(placesData); // Update state with the fetched Place objects

                console.log('Number of places:', placesData.length);

                if (placesData.length > 0) {
                    // Calculate map bounds for the places
                    const bounds = placesData.map((place) => [place.lat, place.lon]);
                    const leafletBounds = L.latLngBounds(bounds);

                    // Adjust the map to fit the bounds (if user moved while fetching data)
                    if (mapRef.current) {
                        mapRef.current.fitBounds(leafletBounds);
                    }
                    setNoPlacesFoundMessage('');
                } else {
                    setNoPlacesFoundMessage(`No missing labels found in this area.`);
                }
            })
            .catch((err) => {
                if (err.message === 'Query cancelled by user') {
                    setErrorMessage('Search cancelled by user');
                } else {
                    setErrorMessage(`${err.message}`);
                }
            })
            .finally(() => {
                setIsLoading(false);
                setAbortController(null); // Clear the abort controller
            });
    };

    const handleSaveLabel = () => {
        if (!modifiedPlaceName.trim()) {
            setShowEmptyInputError(true);
            return;
        }

        setShowEmptyInputError(false);
        setOpenDialog(false);

        // Find the place object from the list
        const placeObject = places.find((city) => city.wikidataLabel === originalPlaceName);

        // Check if place object exists
        if (!placeObject) {
            setErrorMessage('Place not found. Please try again.');
            return;
        }

        // Create a new AddedLabel object
        const newAddedLabel = new AddedLabel(
            modifiedPlaceName, // Use the new label
            placeObject, // Corresponding Place object
            selectedLanguage || customLanguageObject // Ensure custom language is included
        );

        // Convert to JSON for storage
        const newLabelJson = JSON.stringify(newAddedLabel);

        // Remove old entry (if exists) before adding new one
        const updatedSavedPlaces = new Set([...savedPlaces].filter(savedPlace => {
            const parsedPlace = JSON.parse(savedPlace);
            return parsedPlace.place.wikidataID !== placeObject.wikidataID;
        }));

        updatedSavedPlaces.add(newLabelJson); // Add new entry

        // Update state
        setSavedPlaces(updatedSavedPlaces);

        // Update statistics
        const updatedStats = new Statistics();
        Object.assign(updatedStats, statistics);
        updatedStats.addContribution(newAddedLabel);
        setStatistics(updatedStats);
        saveStatistics(updatedStats);

        // Clear inputs
        setModifiedPlaceName('');
        setOriginalPlaceName('');
    };

    const handleMarkerClick = (place) => {
        setSelectedPlace(place); // Store the complete place object
        setOriginalPlaceName(place.wikidataLabel); // Set the original name
        setShowEmptyInputError(false); // Reset input error state

        // Check if the place is already saved and retrieve the modified name
        const savedEntry = Array.from(savedPlaces).find((savedPlace) => {
            const parsedPlace = JSON.parse(savedPlace);
            return parsedPlace.place.wikidataID === place.wikidataID;
        });

        if (savedEntry) {
            const parsedEntry = JSON.parse(savedEntry);
            setModifiedPlaceName(parsedEntry.label); // Set the user-entered name in the dialog
        } else {
            setModifiedPlaceName(""); // Default to empty if no saved entry exists
        }

        setOpenDialog(true); // Open the dialog
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setShowEmptyInputError(false);
        setModifiedPlaceName('');
        setOriginalPlaceName('');
        setSelectedPlace(null);
    };

    const handleCloseAlert = () => {
        setNoPlacesFoundMessage('');
    };

    const handlePlaceTypeChange = (newPlaceTypes) => {
        setSelectedPlaceTypes(newPlaceTypes);
    };

    const handleIncludeLabeledChange = (event) => {
        setIncludeLabeled(event.target.checked);
    };

    const handleLanguageChange = (event, newValue) => {
        setSelectedLanguage(newValue);
        setCustomLanguageObject(null); // Clear custom language when a predefined one is selected
    };

    const handleLocationSearch = (location) => {
        console.log('Location searched:', location);
        // Optionally trigger a new search when user navigates to a new location
        // You could automatically search for places in the new area
    };

    const handleCustomLanguageChange = useCallback(() => {
        if (customLanguage.trim() && customLanguageCode.trim()) {
            const newCustomLanguage = new Language(
                "",
                "",
                "",
                "",
                customLanguage,
                customLanguageCode
            );
            setCustomLanguageObject(newCustomLanguage);
            setSelectedLanguage(null); // Clear selected language from autocomplete
        }
    }, [customLanguage, customLanguageCode]);

    // Button click
    const handleUploadToWikiDataButtonClick = () => {
        setOpenWikidataDialog(true);
    };

    const handleAddToWikidata = async () => {

        setErrorMessage("");
        setSuccessMessage("");

        // no access token entered
        if (!accessToken.trim()) {
            setErrorMessage("Access token is required.");
            return;
        }

        // no language selected
        if (!selectedLanguage?.ietfLanguageTag) {
            setErrorMessage("Please select a language from the dropdown list. Custom languages are not supported for uploading to WikiData.");
            return;
        }

        // Filter out "seen" entries - only upload actual endonyms with labels
        const validEndonyms = Array.from(savedPlaces)
            .map(savedPlace => JSON.parse(savedPlace))
            .filter(entry => !entry.isSeen && entry.label && entry.label.trim());

        if (validEndonyms.length === 0) {
            setErrorMessage("There are no endonyms to upload. Please add place names to the list before uploading to WikiData. (Places marked as 'seen' are not uploaded)");
            return;
        }

        try {
            setIsLoading(true);
            setUploadProgress(0);

            const totalItems = validEndonyms.length;
            const remainingPlaces = new Set(savedPlaces);
            let successfulUploads = 0;
            let uploadCount = 0; // Counter for max uploads

            for (const endonymEntry of validEndonyms) {
                if (uploadCount >= 10) {
                    console.log("Reached the maximum upload limit of 10.");
                    setErrorMessage("Reached the maximum upload limit of 10.");
                    break; // Stop uploading after 10 items
                }

                const {label, place, language} = endonymEntry;
                const {ietfLanguageTag} = language;

                // Extract only the entity ID from the Wikidata URL (e.g., Q64032)
                const wikidataID = place.wikidataID.replace("http://www.wikidata.org/entity/", "");

                console.log(`Uploading: Label: ${label}, WikidataID: ${wikidataID}, Language Tag: ${ietfLanguageTag}`);

                const result = await addLabel(accessToken, wikidataID, label, ietfLanguageTag);

                console.log(`result: ${JSON.stringify(result, null, 2)}`);

                switch (result) {
                    case AddLabelStatus.SUCCESS:
                        console.log("SUCCESS");
                        successfulUploads++;
                        setUploadProgress(successfulUploads);
                        // Remove successfully uploaded place from remaining places
                        const savedPlaceEntry = JSON.stringify(endonymEntry);
                        remainingPlaces.delete(savedPlaceEntry);
                        setPlaces((prevPlaces) =>
                            prevPlaces.filter((p) => p.wikidataID !== place.wikidataID)
                        ); // Remove pin from map
                        break;

                    case AddLabelStatus.CSRF_TOKEN_ERROR:
                        setErrorMessage("Invalid OAuth access token. The token may be expired or lack 'edit' permissions. Please generate a new OAuth 2.0 access token from your Wikimedia account settings.");
                        break;

                    case AddLabelStatus.CSRF_FETCH_ERROR:
                        setErrorMessage("Failed to retrieve CSRF token. Please verify your OAuth 2.0 access token is valid and has proper permissions.");
                        break;

                    case AddLabelStatus.LABEL_ADD_ERROR:
                        setErrorMessage(`Failed to add label '${label}' for place '${place.name}' in language '${ietfLanguageTag}'.`);
                        break;

                    case AddLabelStatus.CORS_ERROR:
                        setErrorMessage("CORS Error: Unable to connect to Wikidata. This may be due to network issues or service unavailability. Please try again later.");
                        break;

                    case AddLabelStatus.NETWORK_ERROR:
                        setErrorMessage("Network Error: Unable to connect to Wikidata API. Please check your internet connection and ensure the development proxy is working.");
                        break;

                    case AddLabelStatus.UNKNOWN_ERROR:
                        setErrorMessage(`An unknown error occurred while uploading place '${place.name}'. Check browser console for details.`);
                        break;

                    default:
                        setErrorMessage("Unexpected error occurred. Check browser console for details.");
                        break;
                }

                uploadCount++; // Increment the upload counter

                // Wait for 1.5sec
                await new Promise((resolve) => setTimeout(resolve, 1500));
            }

            // Update the remaining places
            setSavedPlaces(new Set(remainingPlaces));

            if (successfulUploads < totalItems) {
                const additionalMessage = `Only ${successfulUploads}/${totalItems} uploads were successful.`;
                setErrorMessage((prevMessage) =>
                    prevMessage ? `${prevMessage}
${additionalMessage}` : additionalMessage
                );
            } else if (remainingPlaces.size === 0) {
                setSuccessMessage("All selected locations were successfully uploaded to WikiData!");
            } else {
                setErrorMessage((prevMessage) => (prevMessage ? prevMessage : "")); // Retain previous messages if any, or clear the error message
            }

        } catch (error) {
            console.error("Error uploading to WikiData:", error);
            setErrorMessage("Failed to upload to WikiData. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseWikidataDialog = () => {
        setOpenWikidataDialog(false);
        setErrorMessage("");
        setAccessToken("");
        setUploadProgress(0);
    };

    const handleUpdateLabel = (addedLabel, newLabel) => {
        // Find the place object from the list
        const placeObject = places.find((place) => place.wikidataID === addedLabel.place.wikidataID);

        // Create updated AddedLabel object
        const updatedAddedLabel = new AddedLabel(
            newLabel,
            placeObject || addedLabel.place, // Use existing place if not found in current places
            selectedLanguage || customLanguageObject
        );

        // Convert to JSON for storage
        const updatedLabelJson = JSON.stringify(updatedAddedLabel);

        // Remove old entry and add updated one
        const updatedSavedPlaces = new Set([...savedPlaces].filter(savedPlace => {
            const parsedPlace = JSON.parse(savedPlace);
            return parsedPlace.place.wikidataID !== addedLabel.place.wikidataID;
        }));

        updatedSavedPlaces.add(updatedLabelJson);

        // Update state
        setSavedPlaces(updatedSavedPlaces);
    };

    // Welcome Screen Handlers
    const handleCloseWelcomeScreen = useCallback(() => {
        setShowWelcomeScreen(false);
        localStorage.setItem('endolink_welcome_seen', 'true');
    }, []);

    const handleGetStarted = useCallback(() => {
        setShowWelcomeScreen(false);
        localStorage.setItem('endolink_welcome_seen', 'true');
        // Focus on the language selector to help user get started
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 300);
    }, []);


    // Side Menu Handlers
    const handleToggleSideMenu = useCallback(() => {
        setSideMenuOpen(!sideMenuOpen);
    }, [sideMenuOpen]);

    const handleCloseSideMenu = useCallback(() => {
        setSideMenuOpen(false);
    }, []);

    const handleShowWelcomeFromMenu = useCallback(() => {
        setShowWelcomeScreen(true);
        setSideMenuOpen(false);
    }, []);

    const handleShowInfoFromMenu = useCallback(() => {
        setInfoDialogOpen(true);
        setSideMenuOpen(false);
    }, []);

    const handleCloseInfoDialog = useCallback(() => {
        setInfoDialogOpen(false);
    }, []);

    const handleUploadFromMenu = useCallback(() => {
        setOpenWikidataDialog(true);
        setSideMenuOpen(false);
    }, []);

    const handleImportFromMenu = useCallback(() => {
        // Trigger the import functionality that already exists
        document.getElementById('upload-input')?.click();
        setSideMenuOpen(false);
    }, []);

    // Gamification handlers
    const handleShowBadges = useCallback(() => {
        setBadgeSystemOpen(true);
        setSideMenuOpen(false);
    }, []);

    const handleShowStatistics = useCallback(() => {
        setStatisticsOpen(true);
        setSideMenuOpen(false);
    }, []);

    const handleCloseBadgeSystem = useCallback(() => {
        setBadgeSystemOpen(false);
        setNewBadges([]); // Clear new badge notifications
    }, []);

    const handleCloseStatistics = useCallback(() => {
        setStatisticsOpen(false);
    }, []);

    const handleDeleteLabel = (addedLabel) => {
        // Remove the label from saved places
        const updatedSavedPlaces = new Set([...savedPlaces].filter(savedPlace => {
            const parsedPlace = JSON.parse(savedPlace);
            return parsedPlace.place.wikidataID !== addedLabel.place.wikidataID;
        }));

        // Update state
        setSavedPlaces(updatedSavedPlaces);
    };

    const handleMarkAsSeen = (place) => {
        if (!place) return;

        // Close the dialog
        setOpenDialog(false);

        // Create a special "seen" entry with empty label to mark the place as seen
        const seenLabel = new AddedLabel(
            "", // Empty label indicates this is just a "seen" marker
            place,
            selectedLanguage || customLanguageObject
        );

        // Convert to JSON for storage
        const seenLabelJson = JSON.stringify({
            ...seenLabel,
            isSeen: true, // Mark this as a "seen" entry
            timestamp: Date.now() // Add timestamp for tracking
        });

        // Remove any existing entry for this place before adding the seen marker
        const updatedSavedPlaces = new Set([...savedPlaces].filter(savedPlace => {
            const parsedPlace = JSON.parse(savedPlace);
            return parsedPlace.place.wikidataID !== place.wikidataID;
        }));

        updatedSavedPlaces.add(seenLabelJson);

        // Update state
        setSavedPlaces(updatedSavedPlaces);

        // Update statistics for seen place
        const updatedStats = new Statistics();
        Object.assign(updatedStats, statistics);
        updatedStats.addSeenPlace();
        setStatistics(updatedStats);
        saveStatistics(updatedStats);

        // Clear inputs
        setModifiedPlaceName('');
        setOriginalPlaceName('');
    };

    const handleMarkAsCorrect = (place) => {
        if (!place) return;

        // Close the dialog
        setOpenDialog(false);

        // Create a special "correct" entry to mark the place as having correct existing label
        const correctLabel = new AddedLabel(
            "", // Empty label indicates this is just a "correct" marker
            place,
            selectedLanguage || customLanguageObject
        );

        // Convert to JSON for storage
        const correctLabelJson = JSON.stringify({
            ...correctLabel,
            isCorrect: true, // Mark this as a "correct" entry
            timestamp: Date.now() // Add timestamp for tracking
        });

        // Remove any existing entry for this place before adding the correct marker
        const updatedSavedPlaces = new Set([...savedPlaces].filter(savedPlace => {
            const parsedPlace = JSON.parse(savedPlace);
            return parsedPlace.place.wikidataID !== place.wikidataID;
        }));

        updatedSavedPlaces.add(correctLabelJson);

        // Update state
        setSavedPlaces(updatedSavedPlaces);

        // Update statistics for marked as correct
        const updatedStats = new Statistics();
        Object.assign(updatedStats, statistics);
        updatedStats.addSeenPlace(); // Treat this as a "seen" action for stats
        setStatistics(updatedStats);
        saveStatistics(updatedStats);

        // Clear inputs
        setModifiedPlaceName('');
        setOriginalPlaceName('');
    };

    const handleShowHierarchy = async (place) => {
        if (!place || !place.wikidataID) {
            setHierarchyError("No Wikidata ID available for this place");
            return;
        }

        setOpenHierarchyDialog(true);
        setHierarchyLoading(true);
        setHierarchyError("");
        setHierarchyData(null);

        try {
            const hierarchyQuery = new HierarchySPARQLQuery(place.wikidataID);
            const queryString = hierarchyQuery.getComprehensiveHierarchyQuery();
            
            console.log('Hierarchy SPARQL Query:', queryString);
            
            const data = await queryDispatcher.query(queryString);
            
            // Process the hierarchy data
            const processedData = data.results.bindings.map((item) => ({
                item: item.item?.value || "",
                itemLabel: item.itemLabel?.value || "Unknown",
                instanceOf: item.instanceOf?.value || null,
                instanceOfLabel: item.instanceOfLabel?.value || null,
                superclass: item.superclass?.value || null,
                superclassLabel: item.superclassLabel?.value || null,
                level: parseInt(item.level?.value) || parseInt(item.depth?.value) || 0
            }));

            console.log('Processed hierarchy data:', processedData);
            setHierarchyData(processedData);
        } catch (error) {
            console.error('Error fetching hierarchy:', error);
            setHierarchyError(error.message || 'Failed to fetch hierarchy data');
        } finally {
            setHierarchyLoading(false);
        }
    };

    const handleCloseHierarchyDialog = () => {
        setOpenHierarchyDialog(false);
        setHierarchyData(null);
        setHierarchyError("");
    };

    useEffect(() => {
        handleCustomLanguageChange();
    }, [customLanguage, customLanguageCode, handleCustomLanguageChange]);

    useEffect(() => {
        if (openDialog) {
            // Delay focusing until after the dialog fully opens
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 100);
        }
    }, [openDialog]);

    const handleDownloadEndonyms = () => {
        const data = Array.from(savedPlaces).map(JSON.parse);
        
        // Create export object with metadata and data
        const exportData = {
            metadata: {
                exportDate: new Date().toISOString(),
                exportTimestamp: Date.now(),
                version: "1.0",
                totalEndonyms: data.length,
                generatedBy: "EndoLink - Linguistic Research Platform"
            },
            endonyms: data
        };
        
        const jsonString = JSON.stringify(exportData, null, 2);
        
        // Create blob and download
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        
        // Create download link with timestamp in filename
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T');
        const dateStr = timestamp[0];
        const timeStr = timestamp[1].split('-').slice(0, 3).join('-'); // HH-MM-SS
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `endonyms_${dateStr}_${timeStr}.json`;
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        setSuccessMessage(`Downloaded ${data.length} endonyms to JSON file with timestamp.`);
    };

    const handleUploadEndonyms = (data) => {
        try {
            let endonyms;
            let metadata = null;
            
            // Check if data has new format with metadata or is legacy format
            if (Array.isArray(data)) {
                // Legacy format - direct array of endonyms
                endonyms = data;
            } else if (data.endonyms && Array.isArray(data.endonyms)) {
                // New format with metadata
                endonyms = data.endonyms;
                metadata = data.metadata;
            } else {
                throw new Error('Invalid file format');
            }
            
            // Convert data to the format expected by the app
            const uniquePlaces = new Set(endonyms.map(JSON.stringify));
            setSavedPlaces(uniquePlaces);
            
            // Create success message with metadata info if available
            let successMsg = `Successfully uploaded ${endonyms.length} endonyms from JSON file.`;
            if (metadata) {
                const exportDate = new Date(metadata.exportDate).toLocaleString();
                successMsg += ` (Exported: ${exportDate})`;
            }
            
            setSuccessMessage(successMsg);
        } catch (error) {
            console.error('Error processing uploaded data:', error);
            setErrorMessage('Error processing uploaded endonyms. Please check the file format.');
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <div style={{
                height: '100vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <Header
                    onToggleMenu={handleToggleSideMenu}
                />

                <div style={{
                    position: 'relative',
                    flex: 1,
                    overflow: 'hidden'
                }}>
                    {/* Full-screen Map Background */}
                    <InteractiveMap
                        mapRef={mapRef}
                        initialMapCoordinates={initialMapCoordinates}
                        places={places}
                        savedPlaces={savedPlaces}
                        onMarkerClick={handleMarkerClick}
                        onLocationSearch={handleLocationSearch}
                    />

                    {/* Modern Control Panel - Left Side - Only show if language is selected */}
                    {(selectedLanguage || customLanguageObject) ? (
                        <ControlPanel
                        selectedPlaceTypes={selectedPlaceTypes}
                        onPlaceTypeChange={handlePlaceTypeChange}
                        onSearchClick={handleOnSearchClick}
                        onStopQuery={handleStopQuery}
                        isLoading={isLoading}
                        selectedLanguage={selectedLanguage}
                        customLanguageObject={customLanguageObject}
                        languages={languages}
                        customLanguage={customLanguage}
                        customLanguageCode={customLanguageCode}
                        onLanguageChange={handleLanguageChange}
                        onCustomLanguageChange={(e) => setCustomLanguage(e.target.value)}
                        onCustomLanguageCodeChange={(e) => setCustomLanguageCode(e.target.value)}
                        onOpenLanguagePanel={() => setShowWelcomeScreen(true)}
                        includeLabeled={includeLabeled}
                        onIncludeLabeledChange={handleIncludeLabeledChange}
                        mapRef={mapRef}
                        onLocationSearch={handleLocationSearch}
                    />
                    ) : (
                        // Show language selection prompt when no language is selected
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 16,
                                left: 16,
                                zIndex: 1000,
                                maxWidth: '420px',
                                width: '100%'
                            }}
                        >
                            <Box
                                sx={{
                                    background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                                    color: 'white',
                                    p: 3,
                                    borderRadius: 3,
                                    textAlign: 'center',
                                    boxShadow: '0 10px 25px rgba(37, 99, 235, 0.3)'
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    Welcome to EndoLink! 🌍
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                                    Please select your language to start contributing place names and exploring the map.
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={() => setShowWelcomeScreen(true)}
                                    sx={{
                                        backgroundColor: 'white',
                                        color: 'primary.main',
                                        fontWeight: 600,
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)'
                                        }
                                    }}
                                >
                                    Select Your Language
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* Endonyms Panel - Right Side */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            zIndex: 1100, // Higher than search controls
                            maxWidth: endonymsListExpanded ? '400px' : '60px',
                            width: endonymsListExpanded ? '100%' : 'auto',
                            maxHeight: 'calc(100vh - 120px)',
                            transition: 'all 0.3s ease-in-out'
                        }}
                    >
                        <EndonymsList
                            savedPlaces={savedPlaces}
                            onDownloadEndonyms={handleDownloadEndonyms}
                            onUploadEndonyms={handleUploadEndonyms}
                            onClearEndonyms={() => {
                                setSavedPlaces(new Set());
                                setSuccessMessage('Cleared all endonyms.');
                            }}
                            onUploadToWikidata={handleUploadToWikiDataButtonClick}
                            onUpdateLabel={handleUpdateLabel}
                            onDeleteLabel={handleDeleteLabel}
                            onMapFocus={(place) => focusMapOnPlace(mapRef, place)}
                            expanded={endonymsListExpanded}
                            onExpandedChange={setEndonymsListExpanded}
                        />
                    </Box>

                    <AlertMessages
                        isLoading={isLoading}
                        noPlacesFoundMessage={noPlacesFoundMessage}
                        errorMessage={errorMessage}
                        successMessage={successMessage}
                        onCloseAlert={handleCloseAlert}
                        onCloseError={() => setErrorMessage('')}
                        onCloseSuccess={() => setSuccessMessage('')}
                    />

                </div>

                <LocationNameDialog
                    open={openDialog}
                    place={selectedPlace}
                    originalPlaceName={originalPlaceName}
                    modifiedPlaceName={modifiedPlaceName}
                    showEmptyInputError={showEmptyInputError}
                    selectedLanguage={selectedLanguage}
                    customLanguageObject={customLanguageObject}
                    onClose={handleCloseDialog}
                    onSave={handleSaveLabel}
                    onPlaceNameChange={(e) => setModifiedPlaceName(e.target.value)}
                    onShowHierarchy={handleShowHierarchy}
                    onMarkAsSeen={handleMarkAsSeen}
                    onMarkAsCorrect={handleMarkAsCorrect}
                />

                <WikidataUploadDialog
                    open={openWikidataDialog}
                    accessToken={accessToken}
                    uploadProgress={uploadProgress}
                    savedPlaces={savedPlaces}
                    errorMessage={errorMessage}
                    successMessage={successMessage}
                    onClose={handleCloseWikidataDialog}
                    onUpload={handleAddToWikidata}
                    onAccessTokenChange={(e) => setAccessToken(e.target.value)}
                />

                <HierarchyDialog
                    open={openHierarchyDialog}
                    onClose={handleCloseHierarchyDialog}
                    place={selectedPlace}
                    hierarchyData={hierarchyData}
                    isLoading={hierarchyLoading}
                    error={hierarchyError}
                />

                <WelcomeDialog
                    open={showWelcomeScreen}
                    onClose={handleCloseWelcomeScreen}
                    onGetStarted={handleGetStarted}
                    languages={languages}
                    selectedLanguage={selectedLanguage}
                    customLanguage={customLanguage}
                    customLanguageCode={customLanguageCode}
                    customLanguageObject={customLanguageObject}
                    onLanguageChange={handleLanguageChange}
                    onCustomLanguageChange={(e) => setCustomLanguage(e.target.value)}
                    onCustomLanguageCodeChange={(e) => setCustomLanguageCode(e.target.value)}
                />

                <SideMenu
                    open={sideMenuOpen}
                    onClose={handleCloseSideMenu}
                    onShowWelcome={handleShowWelcomeFromMenu}
                    onExportData={handleDownloadEndonyms}
                    onImportData={handleImportFromMenu}
                    onUploadToWikidata={handleUploadFromMenu}
                    onShowInfo={handleShowInfoFromMenu}
                    onShowBadges={handleShowBadges}
                    onShowStatistics={handleShowStatistics}
                    savedPlaces={savedPlaces}
                    selectedLanguage={selectedLanguage}
                    customLanguageObject={customLanguageObject}
                    statistics={statistics}
                />

                <BadgeSystem
                    open={badgeSystemOpen}
                    onClose={handleCloseBadgeSystem}
                    statistics={statistics}
                    newBadges={newBadges}
                />

                <StatisticsDashboard
                    open={statisticsOpen}
                    onClose={handleCloseStatistics}
                    statistics={statistics}
                />

                <InfoDialog
                    open={infoDialogOpen}
                    onClose={handleCloseInfoDialog}
                />

            </div>
        </ThemeProvider>
    );
}

export default App;
